// RunVarietyPanel — what a run ACTUALLY received, beside what it declares.
// Plan: docs/plans/BERRY-RUN-VARIETY-PLAN-2026-08-04.md (Stage 0).
//
// A run names one variety and almost every pallet inherits it. Berry crates
// each hold one variety, but at peak they run back-to-back, so a run can end
// up carrying fruit it does not describe — measured on prod 2026-08-04,
// RUN-2026-037 declares FRONTERA while 92% of what went in was YASMIN and
// TWILIGHT.
//
// Nothing here is new information: every tip already knows its own variety.
// This is read-only, it just stops the declared variety being mistaken for
// the fact.

import { useQuery } from '@tanstack/react-query'
import { Layers } from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import {
  buildRunMix, formatShare, mismatchedRuns,
  MIX_STATE_CHIP, MIX_STATE_LABEL,
  type RunVarietyRow, type VarietyShare, type RunMix,
} from '@/lib/runVarietyMix'

/** Enough to size the problem at a glance; the rest are on the runs. */
const BANNER_CAP = 6

/** One variety's slice of a run — bar, share, and weight or container count. */
function ShareRow({ s }: { s: VarietyShare }) {
  return (
    <div className="flex items-center gap-2 text-caption">
      <span className={clsx('w-28 shrink-0 truncate', s.unexpected ? 'text-amber-300' : 'text-fg-secondary')}>
        {s.variety}
      </span>
      <span className="flex-1 h-1.5 rounded-full bg-surface overflow-hidden">
        {/* Floor the width so a sliver of a minority variety is still visible. */}
        <span className={clsx('block h-full rounded-full', s.unexpected ? 'bg-amber-600' : 'bg-emerald-600')}
          style={{ width: `${Math.max(2, Math.round(s.share * 100))}%` }} />
      </span>
      <span className="tabular-nums text-fg-muted w-10 text-right shrink-0">{formatShare(s.share)}</span>
      <span className="tabular-nums text-fg-muted w-20 text-right shrink-0">
        {s.netKg > 0 ? `${Math.round(s.netKg).toLocaleString('en-ZA')} kg` : `${s.containers} cont.`}
      </span>
    </div>
  )
}

function useRunVarietyMix(runId: string, declared: string | null) {
  const q = useQuery({
    queryKey: ['run-variety-mix', runId],
    queryFn: async () => {
      const { data, error } = await supabase.from('run_variety_mix')
        .select('variety, tips, containers, net_kg')
        .eq('run_id', runId)
      if (error) throw error
      return (data ?? []) as RunVarietyRow[]
    },
  })
  return { mix: buildRunMix(declared, q.data ?? []), isPending: q.isPending }
}

const declaredLabel = (mix: RunMix) => mix.declared ?? '—'

function MixChip({ state }: { state: RunMix['state'] }) {
  const chip = MIX_STATE_CHIP[state]
  if (!chip) return null
  return (
    <span className={clsx('text-caption uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold', chip)}>
      {MIX_STATE_LABEL[state]}
    </span>
  )
}

function MixExplanation({ mix }: { mix: RunMix }) {
  if (mix.state === 'mixed') {
    return (
      <p className="text-caption text-fg-muted">
        This run received more than one variety. Pallets built from it inherit <b>{declaredLabel(mix)}</b>.
      </p>
    )
  }
  return (
    <p className="text-caption text-fg-muted">
      Nothing tipped into this run was <b>{declaredLabel(mix)}</b>, but pallets built from it inherit that name.
    </p>
  )
}

/** The consequence, spelled out — this is what someone acts on. */
function MixFootnote({ mix }: { mix: RunMix }) {
  if (!(mix.unexpectedKg > 0)) return null
  return (
    <p className="text-caption text-fg-muted">
      {Math.round(mix.unexpectedKg).toLocaleString('en-ZA')} kg of what went in is not the declared variety.
      Grower reporting and packout % for this run read against <b>{declaredLabel(mix)}</b>.
    </p>
  )
}

interface MixRow extends RunVarietyRow { run_id: string }
interface RunLite { run_id: string; run_number: string; variety: string | null; grower: string | null }

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** One affected run, summarised on a single line for the banner. */
function MismatchRow({ run, mix }: { run: RunLite; mix: RunMix }) {
  return (
    <div className="flex items-baseline gap-2 text-caption">
      <span className="font-mono text-fg-secondary shrink-0">{run.run_number}</span>
      <span className="text-fg-muted truncate flex-1">
        declares <b className="text-fg-muted">{declaredLabel(mix)}</b>
        {' · got '}
        {mix.shares.map((s) => `${s.variety} ${formatShare(s.share)}`).join(', ')}
      </span>
    </div>
  )
}

/**
 * Tenant-level sizing: how many runs received fruit they do not describe.
 *
 * One bulk read rather than a chip per row — the question this answers is
 * "how big is this?", which is a total, not a per-row decoration. Renders
 * nothing when every run is clean, so a citrus packhouse (measured: 0 of 437
 * tips mismatched) never sees it.
 */
export function RunVarietyMismatchBanner({ runs }: { runs: RunLite[] }) {
  const runIds = runs.map((r) => r.run_id)
  const q = useQuery({
    queryKey: ['run-variety-mix-bulk', runIds],
    enabled: runIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('run_variety_mix')
        .select('run_id, variety, tips, containers, net_kg')
        .in('run_id', runIds)
      if (error) throw error
      return (data ?? []) as MixRow[]
    },
  })

  const affected = mismatchedRuns(runs, q.data ?? [])
  if (affected.length === 0) return null

  return (
    <div className="mx-4 mt-3 rounded-xl border border-amber-900/50 bg-amber-950/30 p-3 space-y-2">
      <p className="text-caption text-warning flex items-start gap-1.5">
        <Layers size={13} className="shrink-0 mt-0.5" />
        <span>
          <b>{affected.length} {plural(affected.length, 'run', 'runs')}</b> received fruit that is not the
          variety they declare. Pallets built from them inherit the declared name, and grower reporting
          and packout % read against it.
        </span>
      </p>
      <div className="space-y-1">
        {affected.slice(0, BANNER_CAP).map(({ run, mix }) => (
          <MismatchRow key={run.run_id} run={run} mix={mix} />
        ))}
        {affected.length > BANNER_CAP && (
          <p className="text-caption text-fg-muted">
            …and {affected.length - BANNER_CAP} more. Open a run to see its breakdown.
          </p>
        )}
      </div>
    </div>
  )
}

export default function RunVarietyPanel({ runId, declared }: {
  runId: string
  declared: string | null
}) {
  const { mix, isPending } = useRunVarietyMix(runId, declared)

  // Nothing tipped yet, or a run that matches its declaration — the run info
  // block above already says the variety, and repeating it adds noise.
  if (isPending || mix.state === 'unknown' || mix.state === 'clean') return null

  return (
    <div className="card space-y-2">
      <div className="flex items-center gap-2">
        <Layers size={14} className="text-fg-muted shrink-0" />
        <p className="text-caption uppercase tracking-wider font-semibold text-fg-secondary flex-1">
          What was actually tipped
        </p>
        <MixChip state={mix.state} />
      </div>

      <MixExplanation mix={mix} />

      <div className="space-y-1">
        {mix.shares.map((s) => <ShareRow key={s.variety} s={s} />)}
      </div>

      <MixFootnote mix={mix} />
    </div>
  )
}
